import { ArrowLeft, CircleAlert, Home, ChevronRight, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "./Dialog";
import { useState } from "react";
import React from "react";

type HeaderProps = {
    title: string;
    subtitle?: React.ReactNode;
    onTitleClick?: () => void;
    showBack?: boolean;
    showHome?: boolean;
    info?: string;
};

const Header = ({
    title,
    subtitle,
    onTitleClick,
    showBack = true,
    showHome = true,
    info,

}: HeaderProps) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const isRtl = i18n.dir() === 'rtl';

    return (
        <>
            <header className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-md border-b border-slate-200/60 transition-all duration-300">
                <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 h-16">

                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center bg-slate-100/50 p-1 rounded-xl border border-slate-200/40">
                            {showBack && (
                                <button
                                    onClick={() => navigate(-1)}
                                    className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-violet-600"
                                >
                                    <ArrowLeft className={`w-5 h-5 ${isRtl ? 'rotate-180' : ''}`} />
                                </button>
                            )}

                            {showHome && (
                                <button
                                    onClick={() => navigate("/")}
                                    className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all text-slate-600 hover:text-violet-600"
                                >
                                    <Home className="w-5 h-5" />
                                </button>
                            )}
                        </div>

                        <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2">
                                <h1
                                    onClick={onTitleClick}
                                    className={`text-[15px] font-black text-slate-800 truncate tracking-tight 
                                        ${onTitleClick ? "cursor-pointer hover:text-violet-600 transition-colors" : ""}`}
                                >
                                    {t(title)}
                                </h1>
                            </div>
                            {subtitle && (
                                <div className="text-[11px] font-bold text-slate-400 flex items-center gap-1 uppercase tracking-wider overflow-hidden">
                                    <ChevronRight size={10} className={isRtl ? 'rotate-180' : ''} />
                                    {subtitle}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Link
                            to='https://itqan.ntelgnt.com/'
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 h-10 rounded-xl bg-slate-100/80 text-slate-600 hover:bg-violet-50 hover:text-violet-600 border border-slate-200 transition-all duration-300 shadow-sm"
                        >
                            <span className="text-xs font-bold hidden sm:inline">
                                {("Itqan online")}
                            </span>
                            <ExternalLink className="w-4 h-4" />
                        </Link>

                        {info && (
                            <button
                                onClick={() => setOpen(true)}
                                className="flex items-center justify-center h-10 w-10 p-0 rounded-xl bg-violet-50 text-violet-600 hover:bg-violet-600 hover:text-white border border-violet-100 transition-all duration-300 shadow-sm"
                            >
                                <CircleAlert className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent onClose={() => setOpen(false)}>
                    <div dir="ltr" className="p-4 text-slate-700 leading-relaxed">
                        {info}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Header;